import { ColDef } from '@ag-grid-enterprise/all-modules'
import * as EvaluationUtils from '@cck/backend/dist/evaluation/EvaluationUtils'
import { getOrCreateUser } from '@cck/backend/dist/utils/Admin'
import * as Evaluation from '@cck/common/dist/data/Evaluation'
import { Level, Staff, StaffValueFormatter } from '@cck/common/dist/data/Staff'
import { getKoName } from '@cck/common/dist/data/Translation'
import { Theme, makeStyles } from '@material-ui/core/styles'
import Typography from 'antd/lib/typography'
import _ from 'lodash'
import React from 'react'
import { useCookies } from 'react-cookie'

import { grey } from '../../base/color'
import DividerSelect from '../common/DividerSelect'
import {
  EvaluationSelectAttribute,
  loadEvaluationDetails,
  loadEvaluationNames
} from '../evaluation/common/EvaluationUtils'
import EvaluationTable from '../evaluation/view/EvaluationTable'
import StatusView, { StatisticsRow, StatisticsType } from './StatusView'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: 'white',
    border: grey.border,
    padding: theme.spacing(1),
    margin: theme.spacing(1)
  },
  header: {
    padding: theme.spacing(2)
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(0, 1)
  },
  subTitle: {
    margin: theme.spacing(1, 4)
  },
  progressView: {
    margin: theme.spacing(1)
  }
}))

function getStateColumnMap(staffs: Staff[]): {
  department: ColDef[]
  owner: ColDef[]
  performer: ColDef[]
} {
  const defaultColumns: ColDef[] = [
    { headerName: '총 평가대상', field: 'totalCount', sortable: true },
    {
      headerName: '미수행',
      field: `state.${Evaluation.ProgressState.Unperformed}`,
      sortable: true
    },
    {
      headerName: '수행중',
      field: `state.${Evaluation.ProgressState.Performing}`,
      sortable: true
    },
    {
      headerName: '수행완료',
      field: `state.${Evaluation.ProgressState.Performed}`,
      sortable: true
    },
    {
      headerName: '재작성필요',
      field: `state.${Evaluation.ProgressState.Rejected}`,
      sortable: true
    },
    {
      headerName: '승인완료',
      field: `state.${Evaluation.ProgressState.Approved}`,
      sortable: true
    }
  ]

  return {
    department: _.concat(
      { headerName: getKoName('control', 'department'), field: 'key' },
      defaultColumns
    ),
    owner: _.concat(
      {
        headerName: getKoName('control', 'owner'),
        field: 'key',
        cellRenderer: 'StaffCellRenderer',
        valueFormatter: StaffValueFormatter(staffs),
        filterParams: {
          valueFormatter: StaffValueFormatter(staffs)
        }
      },
      defaultColumns
    ),
    performer: _.concat(
      {
        headerName: getKoName('control', 'performer'),
        field: 'key',
        cellRenderer: 'StaffCellRenderer',
        valueFormatter: StaffValueFormatter(staffs),
        filterParams: {
          valueFormatter: StaffValueFormatter(staffs)
        }
      },
      defaultColumns
    )
  }
}

function getWeakStateColumnMap(
  evaluationType: Evaluation.Type,
  staffs: Staff[]
): {
  department: ColDef[]
  owner: ColDef[]
  performer: ColDef[]
} {
  const defaultColumns: ColDef[] = [
    { headerName: '총 평가대상', field: 'totalCount', sortable: true },
    {
      headerName: EvaluationUtils.weakStateToString(evaluationType, '1'),
      field: 'weakState.1',
      sortable: true
    },
    {
      headerName: EvaluationUtils.weakStateToString(evaluationType, '2'),
      field: 'weakState.2',
      sortable: true
    },
    {
      headerName: EvaluationUtils.weakStateToString(evaluationType, '3'),
      field: 'weakState.3',
      sortable: true
    },
    {
      headerName: EvaluationUtils.weakStateToString(evaluationType, '4'),
      field: 'weakState.4',
      sortable: true
    }
  ]

  if (evaluationType === Evaluation.Type.Operation) {
    defaultColumns.push({
      headerName: EvaluationUtils.weakStateToString(evaluationType, '5'),
      field: 'weakState.5',
      sortable: true
    })
  }

  return {
    department: _.concat(
      { headerName: getKoName('control', 'department'), field: 'key' },
      defaultColumns
    ),
    owner: _.concat(
      {
        headerName: getKoName('control', 'owner'),
        field: 'key',
        cellRenderer: 'StaffCellRenderer',
        valueFormatter: StaffValueFormatter(staffs),
        filterParams: {
          valueFormatter: StaffValueFormatter(staffs)
        }
      },
      defaultColumns
    ),
    performer: _.concat(
      {
        headerName: getKoName('control', 'performer'),
        field: 'key',
        cellRenderer: 'StaffCellRenderer',
        valueFormatter: StaffValueFormatter(staffs),
        filterParams: {
          valueFormatter: StaffValueFormatter(staffs)
        }
      },
      defaultColumns
    )
  }
}

interface Props {
  staffs: Staff[]
}

function calculateRatio<
  T extends Evaluation.ProgressState | Evaluation.OperationWeakState | Evaluation.DesignWeakState
>(countMap: Record<T, number>, toString: (value: T) => string): Record<string, number> {
  const numberOfState = _.sum(_.values(countMap))
  return _.reduce(
    countMap,
    (result, value, key) => {
      result[toString(key as T)] = _.round((value / numberOfState) * 100, 2)
      return result
    },
    {} as Record<string, number>
  )
}

function preprocessDoughnutData(
  evaluationType: Evaluation.Type,
  evaluations: Evaluation.EvaluationItem[]
): {
  state: Record<Evaluation.ProgressState, number>
  weakState: Record<Evaluation.OperationWeakState | Evaluation.DesignWeakState, number>
} {
  const counts: {
    state: Record<Evaluation.ProgressState, number>
    resultState: Record<Evaluation.ResultState, number>
    weakState?:
      | Record<Evaluation.OperationWeakState, number>
      | Record<Evaluation.DesignWeakState, number>
  } = {
    state: _.assign(
      {
        [Evaluation.ProgressState.Unperformed]: 0,
        [Evaluation.ProgressState.Performing]: 0,
        [Evaluation.ProgressState.Performed]: 0,
        [Evaluation.ProgressState.Rejected]: 0,
        [Evaluation.ProgressState.Approved]: 0,
        [Evaluation.ProgressState.Request]: 0,
        [Evaluation.ProgressState.Exported]: 0
      },
      _.countBy(evaluations, 'state.state')
    ),
    resultState: _.assign(
      {
        [Evaluation.ResultState.Default]: 0,
        [Evaluation.ResultState.Normal]: 0,
        [Evaluation.ResultState.Defect]: 0
        // 4: 0,
      },
      _.countBy(evaluations, 'state.resultState')
    ),
    weakState: undefined
  }

  if (evaluationType === Evaluation.Type.Operation) {
    counts.weakState = _.assign(
      {
        [Evaluation.OperationWeakState.Default]: 0,
        [Evaluation.OperationWeakState.Pending]: 0,
        [Evaluation.OperationWeakState.Deficiency]: 0,
        [Evaluation.OperationWeakState.SignificantDeficiency]: 0,
        [Evaluation.OperationWeakState.MaterialWeakness]: 0
      },
      _.countBy(evaluations, 'state.weakState')
    )
  } else {
    counts.weakState = _.assign(
      {
        [Evaluation.DesignWeakState.Default]: 0,
        [Evaluation.DesignWeakState.Pending]: 0,
        [Evaluation.DesignWeakState.Done]: 0,
        [Evaluation.DesignWeakState.Modification]: 0
      },
      _.countBy(evaluations, 'state.weakState')
    )
  }

  return {
    state: calculateRatio(counts.state, EvaluationUtils.progressStateToString),
    weakState: calculateRatio(
      counts.weakState as Record<
        Evaluation.OperationWeakState | Evaluation.DesignWeakState,
        number
      >,
      (value: Evaluation.OperationWeakState | Evaluation.DesignWeakState) =>
        EvaluationUtils.weakStateToString(evaluationType, value)
    )
  }
}

function preprocessEvaluationDataInner(
  target: StatisticsType,
  evaluationType: Evaluation.Type,
  evaluations: Evaluation.EvaluationItem[]
): StatisticsRow[] {
  const groupedEvaluations = _.groupBy(evaluations, (evaluation) => {
    return _.get(evaluation, `state.${target}`)
  })
  const defaultWeakState =
    evaluationType === Evaluation.Type.Operation
      ? {
          [Evaluation.OperationWeakState.Default]: 0,
          [Evaluation.OperationWeakState.Pending]: 0,
          [Evaluation.OperationWeakState.Deficiency]: 0,
          [Evaluation.OperationWeakState.SignificantDeficiency]: 0,
          [Evaluation.OperationWeakState.MaterialWeakness]: 0
        }
      : {
          [Evaluation.DesignWeakState.Default]: 0,
          [Evaluation.DesignWeakState.Pending]: 0,
          [Evaluation.DesignWeakState.Done]: 0,
          [Evaluation.DesignWeakState.Modification]: 0
        }

  const allStates = _.map(groupedEvaluations, (value, key) => {
    const state = _.assign(
      {
        [Evaluation.ProgressState.Unperformed]: 0,
        [Evaluation.ProgressState.Performing]: 0,
        [Evaluation.ProgressState.Performed]: 0,
        [Evaluation.ProgressState.Rejected]: 0,
        [Evaluation.ProgressState.Approved]: 0
      },
      _.countBy(value, 'state.state')
    )
    return {
      key,
      totalCount: _.sum(_.values(state)),
      state,
      weakState: _.assign(_.cloneDeep(defaultWeakState), _.countBy(value, 'state.weakState')),
      resultState: _.assign(
        {
          [Evaluation.ResultState.Default]: 0,
          [Evaluation.ResultState.Normal]: 0,
          [Evaluation.ResultState.Defect]: 0,
          4: 0
        },
        _.countBy(value, 'state.resultState')
      )
    }
  })

  allStates.push({
    key: '합계',
    totalCount: _.sum(_.map(allStates, 'totalCount')),
    state: _.assign(
      {
        [Evaluation.ProgressState.Unperformed]: 0,
        [Evaluation.ProgressState.Performing]: 0,
        [Evaluation.ProgressState.Performed]: 0,
        [Evaluation.ProgressState.Rejected]: 0,
        [Evaluation.ProgressState.Approved]: 0
      },
      _.countBy(evaluations, 'state.state')
    ),
    weakState: _.assign(_.cloneDeep(defaultWeakState), _.countBy(evaluations, 'state.weakState')),
    resultState: _.assign(
      {
        [Evaluation.ResultState.Default]: 0,
        [Evaluation.ResultState.Normal]: 0,
        [Evaluation.ResultState.Defect]: 0,
        4: 0
      },
      _.countBy(evaluations, 'state.resultState')
    )
  })

  return allStates
}

function preprocessEvaluationData(
  evaluationType: Evaluation.Type,
  evaluations: Evaluation.EvaluationItem[]
): {
  doughnutData: {
    state: Record<string, number>
    weakState: Record<string, number>
  }
  department: StatisticsRow[]
  owner: StatisticsRow[]
  performer: StatisticsRow[]
} {
  return {
    doughnutData: preprocessDoughnutData(evaluationType, evaluations),
    department: preprocessEvaluationDataInner('department', evaluationType, evaluations),
    owner: preprocessEvaluationDataInner('owner', evaluationType, evaluations),
    performer: preprocessEvaluationDataInner('performer', evaluationType, evaluations)
  }
}

const EvaluationStatusView: React.FC<Props> = ({ staffs }) => {
  const classes = useStyles()
  const [level, setLevel] = React.useState<Level>(Level.NORMAL)
  const [stateDoughnut, setStateDoughnut] = React.useState<Record<string, number>>({})
  const [weakStateDoughnut, setWeakStateDoughnut] = React.useState<Record<string, number>>({})
  const [statisticData, setStatisticData] = React.useState<{
    department: StatisticsRow[]
    owner: StatisticsRow[]
    performer: StatisticsRow[]
  }>({
    department: [],
    owner: [],
    performer: []
  })

  const [evaluationType, setEvaluationType] = React.useState<Evaluation.Type>(
    Evaluation.Type.Operation
  )
  const [select, setSelect] = React.useState<EvaluationSelectAttribute>({
    loading: false,
    selectedName: Evaluation.BASE_ITEM,
    names: [],
    bases: [],
    items: []
  })
  const [cookies, setCookie, removeCookie] = useCookies([
    'dashboardEvaluationType',
    'dashboardEvaluationName'
  ])

  const stateColumnMap = React.useMemo(() => getStateColumnMap(staffs), [staffs])
  const weakStateColumnMap = React.useMemo(
    () => getWeakStateColumnMap(evaluationType, staffs),
    [staffs, evaluationType]
  )

  React.useEffect(() => {
    getOrCreateUser().then((user) => {
      setLevel(_.get(user, 'level'))
    })
  }, [])

  React.useEffect(() => {
    if (select.items) {
      const { doughnutData, ...rest } = preprocessEvaluationData(evaluationType, select.items)
      setStateDoughnut(doughnutData.state)
      setWeakStateDoughnut(doughnutData.weakState)
      setStatisticData(rest)
    }
  }, [evaluationType, select.items])

  React.useEffect(() => {
    const typeCookie = cookies.dashboardEvaluationType
    const nameCookie = cookies.dashboardEvaluationName
    if (typeCookie && nameCookie) {
      setEvaluationType(typeCookie)
      loadEvaluationNames(typeCookie, nameCookie, select, setSelect)
    } else {
      loadEvaluationNames(evaluationType, undefined, select, setSelect)
    }
  }, [evaluationType])

  const onEvaluationSelect = React.useCallback(
    (evaluationName: string): void => {
      loadEvaluationDetails(evaluationType, evaluationName, select, setSelect)
      setCookie('dashboardEvaluationType', evaluationType)
      setCookie('dashboardEvaluationName', evaluationName)
    },
    [evaluationType, select]
  )

  return (
    <div className={classes.root}>
      <Typography.Title className={classes.header} level={4}>
        운영/설계평가 진행 상태
      </Typography.Title>
      <div className={classes.selectContainer}>
        <DividerSelect
          style={{ marginRight: 16 }}
          defaultName="운영/설계평가 선택"
          defaultValue={Evaluation.BASE_ITEM}
          loading={select.loading}
          value={evaluationType}
          names={['운영평가', '설계평가']}
          values={[Evaluation.Type.Operation, Evaluation.Type.Design]}
          onChange={(value) => {
            if (value === Evaluation.Type.Operation || value === Evaluation.Type.Design) {
              removeCookie('dashboardEvaluationType')
              removeCookie('dashboardEvaluationName')
              setSelect({
                ...select,
                selectedName: Evaluation.BASE_ITEM,
                names: [],
                items: []
              })
              setEvaluationType(value)
            }
          }}
        />
        <DividerSelect
          defaultName={`${Evaluation.getKoEvaluation(evaluationType)} 선택`}
          defaultValue={Evaluation.BASE_ITEM}
          loading={select.loading}
          names={EvaluationUtils.getEvaluationNamesWithDate(select.bases)}
          value={select.selectedName}
          values={select.names}
          onChange={(value) => onEvaluationSelect(value as string)}
        />
      </div>
      {select.selectedName !== Evaluation.BASE_ITEM && select.items && (
        <>
          <Typography.Title className={classes.subTitle} level={5}>
            통제 평가 진행 상황
          </Typography.Title>
          <div className={classes.progressView}>
            <EvaluationTable
              items={select.items || []}
              evaluationType={evaluationType}
              height={300}
              loading={select.loading}
              staffs={staffs}
              type="management"
            />
          </div>
        </>
      )}
      {level === Level.ADMIN && select.selectedName !== Evaluation.BASE_ITEM && select.items && (
        <>
          <Typography.Title className={classes.subTitle} level={5}>
            통제 평가 수행 내역
          </Typography.Title>
          <StatusView
            doughnutTitle="통제 평가 수행 내역 (단위 : %)"
            doughnutData={stateDoughnut}
            columnMap={stateColumnMap}
            tableData={statisticData}
          />
          <Typography.Title className={classes.subTitle} level={5}>
            미비점 평가 수행 내역
          </Typography.Title>
          <StatusView
            doughnutTitle="미비점 평가 수행 내역 (단위 : %)"
            doughnutData={weakStateDoughnut}
            columnMap={weakStateColumnMap}
            tableData={statisticData}
          />
        </>
      )}
    </div>
  )
}

export default EvaluationStatusView
